import { useEffect, useRef, useState } from "react";
// import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import ThemeTabs from "components/Tabs/ThemeTabs";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import { exportDailyLogCSV } from "slices/Warehouse/ProjectWarehouse/DailyLog/thunk";
import { exportInStockCSV } from "slices/Warehouse/ProjectWarehouse/InStock/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import NoWarehousesMessage from "Common/Components/NoWarehousesMessage";
import DailyLog from "./DailyLog/DailyLog";
import InStock from "./InStock/InStock";

const ProjectWarehouse = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const prevTabRef = useRef<number | null>(null);
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("WarehouseProjectWarehouseTab")) || 1
  );

  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔥 Use preserved table state hook with tabIndex for per-tab state preservation
  const tableState = usePreservedTableState({
    key: "projectWarehouse",
    tabIndex: tab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection: "" | "asc" | "desc" | undefined = sorting[0]?.desc
    ? "desc"
    : sorting[0]
    ? "asc"
    : "";

  // 🔥 Warehouse search (optional) - stored separately as it's not part of table state
  const [warehouseIdSearch, setWarehouseIdSearch] = useState("");


  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const warehouse_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.warehouse?.app_name
  );
  const item_inward_permissions =
    warehouse_permissions?.modules_permissions?.find(
      (item: any) =>
        item?.modules_name === app_modules?.warehouse?.modules?.item_inward
    );

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // ✅ Tab switch - reset state when tab changes
  const handleTabChange = (val: number) => {
    setTab(val);
    sessionStorage.setItem("WarehouseProjectWarehouseTab", val.toString());
  };

  // Reset state when tab changes (using useEffect to handle tab changes)
  // This ensures state is cleared when switching tabs, but preserved when navigating away and back
  useEffect(() => {
    // Only reset if tab actually changed (not on initial mount)
    // The hook will have re-initialized with the new tabIndex by this point
    if (prevTabRef.current !== null && prevTabRef.current !== tab) {
      // Clear the state for the new tab
      resetState();
    }
    prevTabRef.current = tab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]); // Only depend on tab, not resetState to avoid infinite loops

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  // Tabs configuration
  const tabs = [{ label: "Daily Log" }, { label: "In Stock" }];

  // ✅ CSV Export
  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Build export parameters based on tab
      let exportParams: any;

      switch (tab) {
        case 1: // Daily Log
          exportParams = {
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? (sortDirection as "asc" | "desc") : "",
            search,
            filters: filterValue,
            columnFilters,
            warehouse_id: warehouseIdSearch,
          };
          await dispatch(exportDailyLogCSV(exportParams)).unwrap();
          break;
        case 2: // In Stock
          exportParams = {
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? (sortDirection as "asc" | "desc") : "",
            search,
            filters: filterValue,
            columnFilters,
            warehouse_id: warehouseIdSearch,
          };
          await dispatch(exportInStockCSV(exportParams)).unwrap();
          break;
        default:
          showCustomSwal({
            icon: "info",
            title: "Invalid Tab",
            text: "Invalid tab selected.",
          });
          return;
      }

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export successful.",
      });
    } catch (error: any) {
      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: error?.message || "Failed to export CSV.",
      });
      console.error("Export error:", error);
    }
  };

  useEffect(() => {
    const fetchInit = async () => {
      try {
        setLoading(true);
        await dispatch(fetchLatestPermissions()).unwrap();
        const { payload } = await dispatch(fetchWarehouseDropdown());
        setWarehouseOptions(payload.data);

        // Check if stored warehouse is still valid (exists in current options)
        const storedWarehouse = sessionStorage.getItem(
          "warehouseProjectWarehouseWarehouseID"
        );
        const isStoredWarehouseValid = storedWarehouse &&
          payload.data.some((warehouse: any) => warehouse.warehouse_id === storedWarehouse);

        if (storedWarehouse && isStoredWarehouseValid) {
          setWarehouseIdSearch(storedWarehouse);
        } else {
          // Clear invalid stored warehouse and auto-select first available
          sessionStorage.removeItem("warehouseProjectWarehouseWarehouseID");
          setWarehouseIdSearch(payload.data[0]?.warehouse_id || "");
          sessionStorage.setItem(
            "warehouseProjectWarehouseWarehouseID",
            payload.data[0]?.warehouse_id || ""
          );
        }
      } catch (error) {
        toast.error("Failed to fetch initial data");
      } finally {
        setLoading(false);
      }
    };
    fetchInit();
  }, [dispatch]);
  
  return (
    <ListingPageContainer>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                {/* Title */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={2}
                  xl={2}
                  className="mb-2 mb-lg-0"
                >
                  <h6 className="card-title mb-0">Warehouse</h6>
                </Col>

                {/* Right-aligned elements container */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={10}
                  xl={10}
                  className="mb-2 mb-lg-0"
                >
                  <Row className="g-2 justify-content-end align-items-center">
                    {/* Search Input Box */}
                    <Col xs={12} sm={12} md={6} lg={3} xl={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    {/* Warehouse Autocomplete */}
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={4}
                      className=""
                      style={{ minWidth: "200px" }}
                    >
                      <WarehouseAutocomplete
                        options={warehouseOptions}
                        value={warehouseIdSearch}
                        loading={loading}
                        disableClearable
                        showWarehouseType={true}
                        sessionStorageKey="warehouseProjectWarehouseWarehouseID"
                        onChange={(warehouseId) => {
                          setWarehouseIdSearch(String(warehouseId));

                          // Reset filters and sorting when warehouse changes
                          setColumnFilters([]);
                          setFilterValue([]);
                          setSorting([]);
                          setPageIndex(0);
                        }}
                      />
                    </Col>

                    {/* Buttons */}
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      lg="auto"
                      xl="auto"
                      className="d-flex gap-2 align-items-center flex-nowrap justify-content-end justify-content-lg-start"
                    >
                      {item_inward_permissions?.permissions?.View === true && (
                        <ActionButton
                          variant="light"
                          label="Export"
                          icon="bi bi-box-arrow-up"
                          handleClick={handleExport}
                        />
                      )}

                      {/* Show Inward button only for GRN */}
                      {tab === 4 &&
                        item_inward_permissions?.permissions?.Create ===
                          true && (
                          <ActionButton
                            label="Inward"
                            icon="bi bi-plus-circle"
                            navigateLink="/warehouse-item-inward-create-grn"
                            variant="primary"
                          />
                        )}
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mt-3">
                {/* Tabs */}
                <Col xs={12}>
                  <ThemeTabs
                    tabs={tabs}
                    value={tab - 1}
                    onChange={(newValue) => handleTabChange(newValue + 1)}
                  />
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : warehouseOptions.length > 0 ? (
                <>
                  {tab === 1 && (
                    <DailyLog
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 2 && (
                    <InStock
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                </>
              ) : (
                <NoWarehousesMessage />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default ProjectWarehouse;
